// Container da page de Configurações (lista de white labels).
// Mescla a fonte NATIVA do portal (useWhiteLabels — mesma lista do
// seletor de lojas do header) com os WLs já configurados no
// notification-app, para exibir TODOS os white labels: os
// configurados com link pro detalhe e os "não configurados" com CTA
// de criação da configuração padrão.
import { useTranslation } from "react-i18next"

import { Skeleton } from "@/components/ui/skeleton"
import useWhiteLabels from "@/hooks/use-whitelabels"

import { useNotificationCenterWhiteLabels } from "../hooks"
import { mergeWhiteLabels } from "../merge-white-labels"
import { WhiteLabelMergedCards } from "./white-label-merged-cards"

// Skeleton no formato real do grid de cards de white label.
const WhiteLabelCardsSkeleton = () => (
  <div className="grid gap-6 @xl:grid-cols-2 @5xl:grid-cols-4">
    {Array.from({ length: 4 }).map((_, index) => (
      <div
        key={`wl-skeleton-${index}`}
        className="space-y-4 rounded-xl border p-6"
      >
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    ))}
  </div>
)

export const Configurations = () => {
  const { t } = useTranslation()
  const configuredQuery = useNotificationCenterWhiteLabels()
  const portalStoresQuery = useWhiteLabels()

  // Loading enquanto qualquer uma das duas fontes carrega.
  const isLoading =
    configuredQuery.isLoading || portalStoresQuery.isLoading
  // A lista nativa do portal é a fonte primária; se ela falha, não há
  // como montar a lista completa. A de configurados pode faltar (WL
  // novo sem config) sem quebrar a tela.
  const isError = portalStoresQuery.isError

  const mergedItems = mergeWhiteLabels(
    portalStoresQuery.data ?? [],
    configuredQuery.data ?? []
  )

  // Ao configurar um WL, refaz a lista de configurados (o WL sai de
  // "não configurado" para "configurado" com o card de detalhe).
  const handleConfigured = async () => {
    await configuredQuery.refetch()
  }

  return (
    <div className="space-y-4 pb-10">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">
          {t(
            "pages.notificationCenter.configurationsPage.title"
          )}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "pages.notificationCenter.configurationsPage.subtitle"
          )}
        </p>
      </header>

      {isLoading && <WhiteLabelCardsSkeleton />}

      {!isLoading && isError && (
        <p className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {t(
            "pages.notificationCenter.configurationsPage.loadError"
          )}
        </p>
      )}

      {!isLoading &&
        !isError &&
        (mergedItems.length ? (
          <WhiteLabelMergedCards
            items={mergedItems}
            onConfigured={handleConfigured}
          />
        ) : (
          <p className="rounded-md border p-8 text-center text-sm text-muted-foreground">
            {t(
              "pages.notificationCenter.configurationsPage.emptyState"
            )}
          </p>
        ))}
    </div>
  )
}
